import {fs, csv, glob, dialog} from "./electron-modules";
import path from 'path';
import slash from "slash";

export interface FormattedCameraRow {
  readonly path: string;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly roll: number;
  readonly pitch: number;
  readonly yaw: number;
  readonly name: string;
}

const checkImagePath = (directory, filename, suffix, depth = 3) => {
  const results = {
    success: false,
    duplicate: false,
    imagePath: null,
    imageExt: null
  };

  let dirSeperator = "";
  for (var i = 0; i < depth; i++) {
    if (i > 0) {
      dirSeperator += "/*";
    }

    const globPath = `${directory}${dirSeperator}/${filename}${suffix}`;
    const paths = glob.sync(globPath);

    if (paths.length === 1) {
      results.success = true;
      results.imagePath = path.dirname(paths[0]);
      results.imageExt = path.extname(paths[0]);
      return results;
    } else if (paths.length >= 2) {
      results.duplicate = true;
    }
  }

  return results;
};

const parseFinalData = (assetPath, cameraData, cameraNames, resolve) => {
  const directory = path.dirname(assetPath);
  const numCameras = cameraData.length;
  if (numCameras === 0) {
    resolve([]);
  }

  const firstImagePath = cameraNames[0];
  const firstImageExt = path.extname(firstImagePath).toLowerCase();
  const hasImageExt = [".jpg", ".png", ".jpeg"].includes(firstImageExt);
  const suffix = hasImageExt ? "" : ".{png,jpg,jpeg}";

  // Search for image directory using csv path
  let {success, duplicate, imagePath, imageExt}
    = checkImagePath(directory, firstImagePath, suffix);

  console.log("Detected image path: ", imagePath);

  if (!success) {
    if (duplicate) {
      dialog.showErrorBox(assetPath, "Multiple image paths found. Image directory must be selected manually");
    } else {
      dialog.showErrorBox(assetPath, "Image path not found. Image directory must be selected manually");
    }

    const results = dialog.showOpenDialogSync({
      title: "Select Image Path",
      properties: ['openDirectory']
    });

    // No folder selected
    if (!results) {
      resolve([]);
      return;
    }

    // Search for image directory using selected folder
    imagePath = slash(results[0]);
    let manual = checkImagePath(imagePath, firstImagePath, suffix, 1);
    success = manual.success;
    imageExt = manual.imageExt;

    if (!success) {
      resolve([]);
      return;
    }
  }

  // Update final camera data path / filenames
  for (let i=0;i<numCameras;i++) {
    let filename = cameraNames[i];

    const filePath = hasImageExt ?
      path.join(imagePath, filename) :
      path.join(imagePath, `${filename}${imageExt}`);

    cameraData[i] = {
      ...cameraData[i],
      path: filePath,
      name: path.basename(filePath)
    };
  }

  resolve(cameraData);
};

export const parseCSV = (csvPath: string, assetPath: string): Promise<FormattedCameraRow[]> => {
  return new Promise((resolve, reject) => {
    const stream = fs.createReadStream(csvPath);
    const csvFilename = path.basename(csvPath);

    let cameraData: FormattedCameraRow[] = [];
    let cameraNames = [];

    csv.parseStream(stream, {headers: true})
      .on('error', error => {
        reject(error);
      })
      .on('end', () => {
        parseFinalData(assetPath, cameraData, cameraNames, resolve);
      })
      .on('data', (row) => {
        const filename = row.filename || row.Filename;

        try {
          const data = {
            path: null, name: null,
            x: parseFloat(row.x || row.X),
            y: parseFloat(row.y || row.Y),
            z: parseFloat(row.z || row.Z),
            roll: parseFloat(row.roll || row.Roll),
            pitch: parseFloat(row.pitch || row.Pitch),
            yaw: parseFloat(row.yaw || row.Yaw),
          };

          cameraData.push(data);
          cameraNames.push(filename);
        } catch (err) {
          console.log(`${csvFilename}: error reading csv line`);
        }
      });
  });
};